import { z } from 'zod';
import { DataClassification, QualityStatus } from './vocabularies';

/**
 * The common entity envelope.
 *
 * Source: Canonical Entity Model §2. Every governed entity carries these fields.
 * They exist so that four questions can always be answered about any record:
 *
 *   Who is it?          id, entityType, businessCode
 *   When was it true?   validFrom / validTo        (business effective time)
 *   When did we learn?  sourceUpdatedAt, createdAt / updatedAt
 *   Can I trust it?     dataClassification, qualityStatus, confidence
 *
 * The four clocks are deliberately independent (Entity Model §5). Technical time
 * records database events; business effective time decides which classification
 * or hierarchy applies to a reporting date; workflow time establishes governance
 * history; source time establishes freshness. Collapsing any two of them breaks
 * as-of reporting.
 */

export const Uuid = z.string().uuid();
export type Uuid = z.infer<typeof Uuid>;

/** ISO-8601 timestamp. Always UTC on the wire; the UI formats to a chosen zone. */
export const Timestamp = z.string().datetime({ offset: true });
export type Timestamp = z.infer<typeof Timestamp>;

/** ISO-8601 calendar date (no time component) for business effective dating. */
export const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
export type IsoDate = z.infer<typeof IsoDate>;

/**
 * Provenance of a record. Held separately from the canonical identity so that a
 * re-import can recognise a record it has seen before without ever deriving the
 * canonical id from a mutable source value.
 */
export const SourceIdentity = z.object({
  /** Registered source or file-feed origin, e.g. 'AURORA-CMDB' or 'FEED:DS-007'. */
  sourceSystem: z.string().min(1).nullable(),
  /** The identifier as the source knows it. Never used as the canonical id. */
  sourceRecordId: z.string().min(1).nullable(),
  /** When the source last changed the record — the basis for freshness. */
  sourceUpdatedAt: Timestamp.nullable(),
  /** Ingestion run that most recently wrote this record. */
  sourceRunId: Uuid.nullable(),
});
export type SourceIdentity = z.infer<typeof SourceIdentity>;

export const AuditStamp = z.object({
  createdAt: Timestamp,
  createdBy: Uuid,
  updatedAt: Timestamp,
  updatedBy: Uuid,
});
export type AuditStamp = z.infer<typeof AuditStamp>;

/**
 * Business effective interval. `validTo === null` means "still in force".
 * As-of traversal uses these dates, never a current-parent column, so that a
 * hierarchy move leaves prior reporting reproducible.
 */
export const EffectiveInterval = z.object({
  validFrom: IsoDate,
  validTo: IsoDate.nullable(),
});
export type EffectiveInterval = z.infer<typeof EffectiveInterval>;

export const CanonicalEnvelope = z
  .object({
    id: Uuid,
    /** Multi-tenant discriminator. Loading a second carrier is data, not a fork. */
    tenantId: z.string().min(1),
    entityType: z.string().min(1),
    /** Human-recognisable code, unique within entity type and scope. Changes are audited. */
    businessCode: z.string().min(1).nullable(),
    name: z.string().min(1).nullable(),
    description: z.string().nullable(),
    status: z.string().min(1),
    /** Optimistic concurrency token. Mutations must supply the expected value. */
    rowVersion: z.number().int().nonnegative(),
    dataClassification: DataClassification,
    qualityStatus: QualityStatus,
    /** 0–1. Null where confidence is not meaningful for the entity. */
    confidence: z.number().min(0).max(1).nullable(),
    /** Governed extension fields. Not a substitute for core schema. */
    extensionAttributes: z.record(z.unknown()).nullable(),
  })
  .merge(EffectiveInterval)
  .merge(SourceIdentity)
  .merge(AuditStamp);

export type CanonicalEnvelope = z.infer<typeof CanonicalEnvelope>;

/**
 * Compose an entity schema onto the envelope.
 *
 * Using a helper rather than repeating `.merge(CanonicalEnvelope)` in sixty-one
 * files means the envelope can gain a field in one place and every entity picks
 * it up — which is the point of having an envelope at all.
 */
export function withEnvelope<T extends z.ZodRawShape>(
  entityType: string,
  shape: T,
): z.ZodObject<T & typeof CanonicalEnvelope.shape> {
  return CanonicalEnvelope.extend(shape).extend({
    entityType: z.literal(entityType),
  }) as unknown as z.ZodObject<T & typeof CanonicalEnvelope.shape>;
}

/** Fields a client may supply when creating an entity. The server owns the rest. */
export const CreateEnvelopeInput = z.object({
  businessCode: z.string().min(1).optional(),
  name: z.string().min(1),
  description: z.string().optional(),
  validFrom: IsoDate,
  validTo: IsoDate.nullable().optional(),
  extensionAttributes: z.record(z.unknown()).optional(),
});
export type CreateEnvelopeInput = z.infer<typeof CreateEnvelopeInput>;

/** Every mutation carries the row version it believes it is changing. */
export const ConcurrencyToken = z.object({
  rowVersion: z.number().int().nonnegative(),
});
export type ConcurrencyToken = z.infer<typeof ConcurrencyToken>;

/**
 * Material changes carry a reason. This is what makes the audit trail readable
 * six months later, and it is required by the governance contract.
 */
export const ChangeContext = z.object({
  reason: z.string().min(1),
  changeReference: z.string().optional(),
});
export type ChangeContext = z.infer<typeof ChangeContext>;
